import { Link, useLocation } from 'react-router-dom';
import { tabItemClass, tabCountClass } from './ui/tabStyles';

export interface SubTab {
  to: string;
  label: string;
  count?: number;
  amber?: boolean;
}

interface SubTabBarProps {
  /** Absolute base path that sub-tab `to` values are appended to.
   *  Example: "/cases/26/discovery". Required — do NOT rely on router
   *  resolution here, because v7 splat routes return the full URL
   *  (including the splat segment) from useResolvedPath(''). */
  basePath: string;
  tabs: SubTab[];
  /** If true, preserve location.search across sub-tab navigation. Default true. */
  preserveSearch?: boolean;
}

/** Second-level nav under the case header. Router-link twin of ui/Tabs —
 *  renders the same underline language via the shared class recipes. */
export default function SubTabBar({ basePath, tabs, preserveSearch = true }: SubTabBarProps) {
  const location = useLocation();
  const search = preserveSearch ? location.search : '';

  const normalizedBase = basePath.replace(/\/+$/, '');
  const rest = location.pathname.startsWith(normalizedBase + '/')
    ? location.pathname.slice(normalizedBase.length + 1)
    : location.pathname === normalizedBase
      ? ''
      : '';
  const currentSegment = rest.split('/')[0] || '';

  return (
    <div className="border-b border-border bg-surface-card">
      <nav aria-label="Section" className="-mb-px flex overflow-x-auto">
        {tabs.map((tab) => {
          const isAmber = tab.amber && (tab.count ?? 0) > 0;
          const isActive = currentSegment === tab.to;
          const fullPath = `${normalizedBase}/${tab.to}${search}`;
          return (
            <Link
              key={tab.to}
              to={fullPath}
              aria-current={isActive ? 'page' : undefined}
              className={tabItemClass({ active: isActive, amber: isAmber, inset: true })}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={tabCountClass({ active: isActive, amber: isAmber })}>
                  {tab.count}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
